"use client"

import { useMemo, useState } from "react"
import { createRoute } from "@tanstack/react-router"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { CandidateProfileOverlayContainer } from "@/components/candidate-profile-overlay"
import { EditTaskDialog } from "@/components/edit-task-dialog"
import { JobHiringManagersDialog } from "@/components/job-hiring-managers"
import { JobFileUpload } from "@/components/job-file-upload"
import { JobNotes } from "@/components/job-notes"
import { NewTaskDialog } from "@/components/new-task-dialog"
import { JobPipelineTab } from "@/components/job-pipeline-tab"
import { JobHeaderActionProvider } from "@/components/jobs/job-header-action"
import {
  GraphiteJobConsole,
  GraphiteJobHeader,
} from "@/components/jobs/graphite-job-console"
import type { ActivityTarget } from "@/components/jobs/graphite-job-console-data"
import { useCandidateOverlayUrlState } from "@/hooks/use-candidate-overlay"
import { useJobConsole } from "@/hooks/use-job-console"
import MainAppRoute from "@/rootRoutes/MainApp"
import { ValidateSearch } from "@/utils/candidate-profile-overlay-utils"
import {
  useDeleteTasksTaskId,
  useGetJobsJobId,
  usePostTasks,
  usePutTasksTaskId,
} from "openapi/api/endpoints"
import type { JobTask, Task } from "openapi/api/model"
import { invalidateTaskQueries } from "@/lib/query-client"

/** A job's task as the shared task editor takes it (both come from `tasks`). */
function toEditableTask(task: JobTask): Task {
  return {
    id: task.id,
    task_id: task.id,
    title: task.title,
    description: task.description,
    due_date: task.due_date,
    status: task.status,
    priority: task.priority as Task["priority"],
    entity_type: task.entity_type,
    candidate_name: task.candidate_name,
  }
}

const JobPageRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/jobs/$jobId/$view",
  component: JobOverviewPageComponent,
  validateSearch: ValidateSearch,
})
export default JobPageRoute

const VALID_VIEWS = ["overview", "pipeline", "files", "notes"] as const
type JobViews = (typeof VALID_VIEWS)[number]

/**
 * Canvas 4a labels the first tab "Today", not "Overview" — the tab is what needs
 * doing now, not a summary of what exists.
 *
 * 4a also draws a "Files" tab, and a note here used to say it "has no route in
 * this app yet". That was wrong in the way that matters: the route did not
 * exist because the redesign dropped it. The old overview mounted
 * `JobFileUpload` inline, and replacing that overview left the component in the
 * tree with nothing rendering it — so uploading, renaming, downloading and
 * deleting a job's files all became unreachable while their endpoints went on
 * working. A restyle does not get to remove a feature.
 *
 * Four tabs, in the canvas's order. Outreach, Analytics and Settings were tabs
 * too, each opening a "Coming soon" card — three of seven tabs that went
 * nowhere, and on a phone they pushed the real ones off screen. An old link to
 * one of them lands on Today.
 */
const JOB_TABS = [
  { id: "overview", label: "Today" },
  { id: "pipeline", label: "Pipeline" },
  { id: "files", label: "Files" },
  { id: "notes", label: "Notes" },
]

function JobOverviewPageComponent() {
  const params = JobPageRoute.useParams()
  /*
    The console's quiet-job alert names the thing to do and offers "Add task"
    beside it; the composer that does it lives here, because the console is
    presentational and the page is what owns requests.
  */
  const [newTaskOpen, setNewTaskOpen] = useState(false)
  const queryClient = useQueryClient()
  const { mutateAsync: createTask } = usePostTasks()
  const search = JobPageRoute.useSearch()
  const navigate = JobPageRoute.useNavigate()
  const { data } = useGetJobsJobId(params.jobId)
  const console_ = useJobConsole(params.jobId)
  const { view } = params

  /*
    The candidate overlay belongs to the page, not to one tab, so Today's task
    and activity rows open the same profile the pipeline does. It is addressed
    by the URL (candidateId, tab, commentId), like everywhere else it opens.
  */
  const overlay = useCandidateOverlayUrlState({
    rawSearch: search,
    setSearch: (updater) => navigate({ search: updater }),
  })

  /*
    Today's tasks open in the same editor the Ops board uses; saving or
    deleting refreshes every list a task appears in.
  */
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  const [managersOpen, setManagersOpen] = useState(false)
  const openTask = openTaskId ? console_.tasksById.get(openTaskId) : undefined
  const { mutateAsync: putTask } = usePutTasksTaskId()
  const { mutateAsync: deleteTask } = useDeleteTasksTaskId()

  const openActivity = (target: ActivityTarget) => {
    if (target.kind === "comment") {
      overlay.openCandidateAt(target.candidateId, { commentId: target.commentId })
    } else if (target.kind === "profile") {
      overlay.openCandidateAt(target.candidateId, { tab: target.tab })
    } else {
      switchTab("files")
    }
  }

  /*
    Converted once per task object, not per render: the editor resets its form
    whenever `task` changes identity, so a fresh object each render would wipe
    an edit in progress. The query keeps a task's object across refetches that
    return the same data.
  */
  const editableTask = useMemo(() => (openTask ? toEditableTask(openTask) : null), [openTask])

  const activeView: JobViews = VALID_VIEWS.includes(view as JobViews)
    ? (view as JobViews)
    : "overview"

  const switchTab = (next: string) => {
    navigate({ to: `/jobs/${params.jobId}/${next}`, replace: true })
  }

  if (!data) {
    return <div className="p-6 text-sm text-muted-foreground">Job not found</div>
  }

  /*
    The whole console, from the job. This used to spread `canvasJobConsoleData`
    and replace only the header, so every job on the instance showed Northwind's
    pipeline, tasks, activity, description and hiring managers — plausible
    records belonging to no actual job, which a recruiter acts on.

    The hook aggregates all five requests, and this has to *consume* that: an
    unresolved or failed pipeline, task, activity or hiring-manager request
    still yields a truthy `data` built from empty fallbacks, so gating on the
    data alone put zero counts and empty sections on screen as facts — the
    previous round's fix, left half-done.
  */
  /*
    The header is the job's own record, so it renders as soon as that lands.
    Gating the whole page on the console's five requests held up Pipeline, Notes
    and Files too — tabs that never read any of them.
  */
  const header = {
    title: data.title,
    subtitle:
      [data.company_name, data.department, data.location, data.type, data.salary_range]
        .filter(Boolean)
        .join(" · ") || "—",
    company: data.company_name ?? undefined,
    status: data.status ?? "—",
  }

  return (
    /*
      The container every job view measures itself against. These pages live
      inside the ATS resizable panel, so a viewport breakpoint would switch on
      the window rather than on the space the page actually has.
    */
    <JobHeaderActionProvider>
    <div className="@container flex h-full min-h-0 flex-col">
      <GraphiteJobHeader
        data={header}
        jobId={params.jobId}
        activeTab={activeView}
        onTabChange={switchTab}
        tabs={JOB_TABS}
      />

      <div className="min-h-0 flex-1 overflow-hidden">
        {activeView === "overview" &&
          (console_.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Reading the job…</p>
          ) : console_.isError || !console_.data ? (
            <p className="p-6 text-sm text-muted-foreground">
              This job cannot be read right now, so nothing here would be worth
              trusting.
            </p>
          ) : (
            <GraphiteJobConsole
              data={console_.data}
              sections={console_.sections}
              onAddTask={() => setNewTaskOpen(true)}
              onOpenTask={setOpenTaskId}
              onOpenActivity={openActivity}
              onOpenCandidate={overlay.openCandidate}
              onManageHiringManagers={() => setManagersOpen(true)}
              onOpenSearch={(hash) =>
                navigate({
                  to: "/searches/$searchId/results",
                  params: { searchId: hash },
                  search: { page: 1 },
                })
              }
            />
          ))}

        {activeView === "pipeline" && (
          // The board manages its own scrolling — horizontal across lanes,
          // vertical inside each — so this wrapper must not add another.
          <div className="h-full overflow-hidden px-4 pt-3 @3xl:px-6 @3xl:pt-3.5">
            <JobPipelineTab jobId={params.jobId} onOpenCandidate={overlay.openCandidate} />
          </div>
        )}

        {/* 18a owns its own padding and scrolling — the notes list scrolls
            under a fixed count row rather than the whole screen moving. */}
        {activeView === "notes" && (
          <JobNotes jobId={params.jobId} jobTitle={data.title} />
        )}

        {/* Files owns its own card; the tab gives it the page's padding and
            the scroll. */}
        {activeView === "files" && (
          <div className="h-full overflow-y-auto p-3.5 @3xl:p-4">
            <JobFileUpload jobId={params.jobId} />
          </div>
        )}

      </div>
    </div>
      <NewTaskDialog
        open={newTaskOpen}
        onOpenChange={setNewTaskOpen}
        jobId={params.jobId}
        jobLabel={data.title}
        onCreateTask={async (task) => {
          try {
            await createTask({ data: task })
            await invalidateTaskQueries(queryClient)
            toast.success("Task created")
          } catch (error) {
            toast.error("The task could not be created", {
              description: `${error}`,
            })
            // Rethrown so the composer keeps the form. Swallowed, it resolves
            // as a success and closes a filled-in dialog on a task that was
            // never created.
            throw error
          }
        }}
      />
      <EditTaskDialog
        open={Boolean(editableTask)}
        onOpenChange={(open) => !open && setOpenTaskId(null)}
        task={editableTask}
        onUpdateTask={async (taskId, updates) => {
          try {
            await putTask({ taskId, data: updates })
            await invalidateTaskQueries(queryClient)
            toast.success("Task updated")
          } catch (error) {
            toast.error("The task could not be updated", { description: `${error}` })
            throw error
          }
        }}
        onDeleteTask={async (taskId) => {
          try {
            await deleteTask({ taskId })
            await invalidateTaskQueries(queryClient)
            setOpenTaskId(null)
            toast.success("Task deleted")
          } catch (error) {
            toast.error("The task could not be deleted", { description: `${error}` })
            throw error
          }
        }}
      />
      <JobHiringManagersDialog
        jobId={params.jobId}
        open={managersOpen}
        onOpenChange={setManagersOpen}
      />
      <CandidateProfileOverlayContainer
        candidateId={overlay.candidateId}
        jobId={params.jobId}
        onClose={overlay.closeOverlay}
        activeTab={overlay.tab}
        onTabChange={overlay.setTab}
        activeCommentMode={overlay.commentMode}
        activeCommentId={overlay.commentId ?? null}
        onCommentChange={overlay.setComment}
      />
    </JobHeaderActionProvider>
  )
}
